import { translate } from "@/i18n";
import { shrinkImage } from "@/lib/shrinkImage";

// The most one file may weigh, as the server holds it (MediaFile::MAX_BYTES), checked before anything
// goes up so a file that would be refused leaves nothing in the bucket.
export const MAX_MB = { image: 30, video: 500 } as const;
const VIDEO = /^video\/(mp4|quicktime|webm)$/;

// His `shrinkPhoto`: a photo made a 1920 px JPEG; one the browser cannot read goes up as it is only
// when it is a JPEG, PNG or WebP — anything else (a HEIC on a PC) is his "take it with the camera".
export async function prepareJobPhoto(file: File): Promise<File> {
  try {
    return await shrinkImage(file, 1920);
  } catch {
    if (/^image\/(png|jpeg|webp)$/.test(file.type)) return file;
    throw new Error(translate("mediaJob.photoType"));
  }
}

// A job's photo or video (the job's own, a line's, an issue's), ready to go up — or his message saying
// why not. His `mFile` takes images and videos only, as a run or an issue holds (no PDF).
export async function prepareJobFile(file: File): Promise<File> {
  const type = file.type || (/\.(mp4|mov|m4v|webm)$/i.test(file.name) ? "video/mp4" : "");
  const mb = (f: File) => Math.ceil(f.size / 1_048_576);
  if (type.startsWith("image/") || /\.hei[cf]$/i.test(file.name)) {
    const photo = await prepareJobPhoto(file);
    if (mb(photo) > MAX_MB.image) throw new Error(translate("mediaJob.tooBig", { mb: mb(photo), max: MAX_MB.image }));
    return photo;
  }
  if (type.startsWith("video/") || /\.(mp4|mov|m4v|webm)$/i.test(file.name)) {
    if (!VIDEO.test(type) && !/\.(mp4|mov|m4v|webm)$/i.test(file.name)) throw new Error(translate("mediaJob.videoType"));
    if (mb(file) > MAX_MB.video) throw new Error(translate("mediaJob.videoBig", { mb: mb(file), max: MAX_MB.video }));
    return file;
  }
  throw new Error(translate("mediaJob.fileType"));
}
